// path: src/Components/endless/EndlessSetup.js

import React, { useEffect, useMemo, useState } from "react";
import {
    getUnlockRequirementText,
    isEndlessBoardSizeUnlocked,
    isModifierUnlocked,
} from "../../utils/storyProgress";
import { buildEndlessRunConfig } from "../../utils/runConfig";
import { buildEndlessScorePreview } from "../../utils/endlessScore";
import LockIcon from "../LockIcon";

const BOARD_OPTIONS = [
    { size: 4, label: "2×2", unlockKey: "board4" },
    { size: 9, label: "3×3", unlockKey: "board9" },
    { size: 16, label: "4×4", unlockKey: "board16" },
];

export default function EndlessSetup({
    progress,
    onStart,
    onCancel,
}) {
    const [boardSize, setBoardSize] = useState(null);
    const [twistedMode, setTwistedMode] = useState(false);
    const [shyCorgisMode, setShyCorgisMode] = useState(false);
    const [anxiousMode, setAnxiousMode] = useState(false);
    const [hardcoreMode, setHardcoreMode] = useState(false);
    const [blitzMode, setBlitzMode] = useState(false);
    const [liveMessage, setLiveMessage] = useState("");

    const board4Unlocked = isEndlessBoardSizeUnlocked(4, progress);
    const board9Unlocked = isEndlessBoardSizeUnlocked(9, progress);
    const board16Unlocked = isEndlessBoardSizeUnlocked(16, progress);
    const twistedUnlocked = isModifierUnlocked("twistedMode", progress);
    const shyUnlocked = isModifierUnlocked("shyCorgisMode", progress);
    const anxiousUnlocked = isModifierUnlocked("anxiousMode", progress);
    const hardcoreUnlocked = isModifierUnlocked("hardcoreMode", progress);
    const blitzUnlocked = isModifierUnlocked("blitzMode", progress);
    const anyBoardUnlocked =
        board4Unlocked || board9Unlocked || board16Unlocked;

    const effectiveHardcore = blitzMode ? true : hardcoreMode;
    const scorePreview = useMemo(() => {
        if (boardSize == null) {
            return null;
        }

        return buildEndlessScorePreview(boardSize, {
            twistedMode: twistedUnlocked && twistedMode,
            shyCorgisMode: shyUnlocked && shyCorgisMode,
            anxiousMode: anxiousUnlocked && anxiousMode,
            hardcoreMode: hardcoreUnlocked && effectiveHardcore,
            blitzMode: blitzUnlocked && blitzMode,
        });
    }, [
        boardSize,
        twistedMode,
        shyCorgisMode,
        anxiousMode,
        effectiveHardcore,
        blitzMode,
        twistedUnlocked,
        shyUnlocked,
        anxiousUnlocked,
        hardcoreUnlocked,
        blitzUnlocked,
    ]);

    useEffect(() => {
        if (
            boardSize == null ||
            !isEndlessBoardSizeUnlocked(boardSize, progress)
        ) {
            const preferredBoard = board4Unlocked
                ? 4
                : board9Unlocked
                ? 9
                : board16Unlocked
                ? 16
                : null;
            setBoardSize(preferredBoard);
        }

        if (!twistedUnlocked) {
            setTwistedMode(false);
        }

        if (!shyUnlocked) {
            setShyCorgisMode(false);
        }

        if (!anxiousUnlocked) {
            setAnxiousMode(false);
        }

        if (!hardcoreUnlocked) {
            setHardcoreMode(false);
        }

        if (!blitzUnlocked) {
            setBlitzMode(false);
        }
    }, [
        boardSize,
        board4Unlocked,
        board9Unlocked,
        board16Unlocked,
        twistedUnlocked,
        shyUnlocked,
        anxiousUnlocked,
        hardcoreUnlocked,
        blitzUnlocked,
        progress,
    ]);

    const handleStart = () => {
        if (
            boardSize == null ||
            !isEndlessBoardSizeUnlocked(boardSize, progress)
        ) {
            setLiveMessage(
                getUnlockRequirementText(
                    boardSize === 4
                        ? "board4"
                        : boardSize === 9
                        ? "board9"
                        : boardSize === 16
                        ? "board16"
                        : "board4"
                )
            );
            return;
        }

        if (twistedMode && !twistedUnlocked) {
            setLiveMessage(getUnlockRequirementText("twistedMode"));
            return;
        }

        if (shyCorgisMode && !shyUnlocked) {
            setLiveMessage(getUnlockRequirementText("shyCorgisMode"));
            return;
        }

        if (anxiousMode && !anxiousUnlocked) {
            setLiveMessage(getUnlockRequirementText("anxiousMode"));
            return;
        }

        if (hardcoreMode && !hardcoreUnlocked && !blitzMode) {
            setLiveMessage(getUnlockRequirementText("hardcoreMode"));
            return;
        }

        if (blitzMode && !blitzUnlocked) {
            setLiveMessage(getUnlockRequirementText("blitzMode"));
            return;
        }

        const runConfig = buildEndlessRunConfig({
            boardSize,
            twistedMode: twistedUnlocked && twistedMode,
            shyCorgisMode: shyUnlocked && shyCorgisMode,
            anxiousMode: anxiousUnlocked && anxiousMode,
            hardcoreMode: blitzMode
                ? true
                : hardcoreUnlocked && hardcoreMode,
            blitzMode: blitzUnlocked && blitzMode,
        });

        setLiveMessage("Starting Endless run.");
        onStart(runConfig);
    };

    const boardModifiers = [
        {
            id: "twisted",
            label: "Twisted",
            chipClass: "gameStatsChip--twisted",
            checked: twistedMode,
            unlocked: twistedUnlocked,
            disabled: !twistedUnlocked,
            title: twistedUnlocked
                ? "Cards randomly rotate after each shuffle."
                : getUnlockRequirementText("twistedMode"),
            onChange: setTwistedMode,
        },
        {
            id: "shy",
            label: "Shy",
            chipClass: "gameStatsChip--shy",
            checked: shyCorgisMode,
            unlocked: shyUnlocked,
            disabled: !shyUnlocked,
            title: shyUnlocked
                ? "Cards randomly flip face-down for a few seconds."
                : getUnlockRequirementText("shyCorgisMode"),
            onChange: setShyCorgisMode,
        },
        {
            id: "anxious",
            label: "Anxious",
            chipClass: "gameStatsChip--anxious",
            checked: anxiousMode,
            unlocked: anxiousUnlocked,
            disabled: !anxiousUnlocked,
            title: anxiousUnlocked
                ? "The board scrambles on its own every few seconds."
                : getUnlockRequirementText("anxiousMode"),
            onChange: setAnxiousMode,
        },
    ];

    const ruleModifiers = [
        {
            id: "hardcore",
            label: "Hardcore",
            chipClass: "gameStatsChip--hardcore",
            checked: blitzMode ? true : hardcoreMode,
            unlocked: hardcoreUnlocked,
            disabled: !hardcoreUnlocked || blitzMode,
            title: hardcoreUnlocked
                ? blitzMode
                    ? "Always active for Blitz."
                    : "Wrong cards immediately end the run."
                : getUnlockRequirementText("hardcoreMode"),
            onChange: setHardcoreMode,
        },
        {
            id: "blitz",
            label: "Blitz",
            chipClass: "gameStatsChip--blitz",
            checked: blitzMode,
            unlocked: blitzUnlocked,
            disabled: !blitzUnlocked,
            title: blitzUnlocked
                ? "2-minute timed challenge. Hardcore is always active."
                : getUnlockRequirementText("blitzMode"),
            onChange: (next) => {
                setBlitzMode(next);
                if (next) {
                    setHardcoreMode(true);
                }
            },
        },
    ];

    const renderModifierToggle = (modifier) => (
        <label
            key={modifier.id}
            className={`endlessModifierToggle${
                modifier.unlocked ? "" : " endlessModifierToggle--locked"
            }${modifier.checked ? " endlessModifierToggle--on" : ""}`}
            title={modifier.title}
        >
            <input
                type="checkbox"
                checked={modifier.checked}
                disabled={modifier.disabled}
                aria-label={modifier.label}
                onChange={(event) => modifier.onChange(event.target.checked)}
            />
            <span className={`gameStatsChip ${modifier.chipClass}`}>
                {modifier.label}
                {!modifier.unlocked ? (
                    <LockIcon className="homeLockIcon" title="" />
                ) : null}
            </span>
        </label>
    );

    return (
        <div className="endlessSetup endlessSetup--runCard" aria-label="Endless Mode setup">
            <section className="endlessSetupSection">
                <h2 className="endlessSetupSection__title">Board</h2>
                <div
                    className="difficultyPicker"
                    role="group"
                    aria-label="Board size"
                >
                    {BOARD_OPTIONS.map((option) => {
                        const unlocked = isEndlessBoardSizeUnlocked(
                            option.size,
                            progress
                        );

                        return (
                            <button
                                key={option.size}
                                type="button"
                                className={`difficultyPickerButton ${
                                    boardSize === option.size
                                        ? "difficultyPickerButton--selected"
                                        : ""
                                }`}
                                disabled={!unlocked}
                                aria-pressed={boardSize === option.size}
                                title={
                                    unlocked
                                        ? option.label
                                        : getUnlockRequirementText(option.unlockKey)
                                }
                                onClick={() => setBoardSize(option.size)}
                            >
                                {option.label}
                                {!unlocked ? (
                                    <LockIcon className="homeLockIcon" title="" />
                                ) : null}
                            </button>
                        );
                    })}
                </div>
                {!anyBoardUnlocked && (
                    <p className="endlessSetupHint" role="status">
                        Clear Story Level 3 to unlock Endless boards.
                    </p>
                )}
            </section>

            <section className="endlessSetupSection">
                <h2 className="endlessSetupSection__title">Modifiers</h2>
                <div
                    className="endlessModifierList endlessModifierList--modifiers"
                    role="group"
                    aria-label="Board modifiers"
                >
                    {boardModifiers.map(renderModifierToggle)}
                </div>
            </section>

            <section className="endlessSetupSection">
                <h2 className="endlessSetupSection__title">Rules</h2>
                <div
                    className="endlessModifierList endlessModifierList--rules"
                    role="group"
                    aria-label="Run rules"
                >
                    {ruleModifiers.map(renderModifierToggle)}
                </div>
            </section>

            <div className="endlessSetupFooter">
                {scorePreview ? (
                    <div className="endlessRunStatus" aria-label="This run">
                        <span>+{scorePreview.pointsPerClick} pts/pick</span>
                        <span className="endlessRunStatus__sep" aria-hidden="true">
                            ·
                        </span>
                        <span>
                            Hearts {scorePreview.heartsAllowed ? "On" : "Off"}
                        </span>
                    </div>
                ) : (
                    <div className="endlessRunStatus endlessRunStatus--empty">
                        Pick a board to preview this run
                    </div>
                )}

                <div className="endlessSetupActions">
                    {typeof onCancel === "function" && (
                        <button
                            type="button"
                            className="homePrimaryButton homeSecondaryButton endlessSetupActions__secondary"
                            onClick={onCancel}
                        >
                            Cancel
                        </button>
                    )}

                    <button
                        type="button"
                        className="homePrimaryButton endlessSetupActions__primary"
                        onClick={handleStart}
                        disabled={!anyBoardUnlocked || boardSize == null}
                    >
                        Start Endless
                    </button>
                </div>
            </div>

            <div className="srOnly" role="status" aria-live="polite">
                {liveMessage}
            </div>
        </div>
    );
}
